import { Location } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { take } from 'rxjs/operators';

import { RequestHelpContext } from '../models';
import { HelpApiService } from './help-api.service';
import { HelpContextService } from './help-context.service';

/**
 * Opens Help without costing the reader their place.
 *
 * WHY HELP OPENS IN A SEPARATE TAB AT ALL. The scenario page holds a deal lock and has
 * a CanDeactivate guard for unsaved changes, so routing away from it to read help can
 * prompt about unsaved work or drop the lock. Context-sensitive help that destroys the
 * context it was helping with is self-defeating, so Help gets its own tab and the
 * working tab is left untouched.
 *
 * WHY THE RESOLUTION HAPPENS HERE, IN THE CALLING TAB. A new tab is a fresh Angular
 * application, and HelpContextService holds its hint in memory per tab. Opening a bare
 * /help in a new tab would therefore arrive with no context and land on the Help home -
 * silently undoing the whole point. So this resolves FIRST, in the tab that knows where
 * the reader is, and opens the new tab directly on the resolved article.
 */
@Injectable({
  providedIn: 'root'
})
export class HelpEntryService {

  // ----------------- Constants ---------------------------------------------

  /**
   * The window name Help reuses.
   *
   * A name rather than _blank on purpose: repeated Help clicks re-point this one tab
   * instead of leaving a trail of tabs to close. A reader who genuinely wants two
   * articles side by side can still ctrl-click the link, because the anchor keeps a
   * real href.
   */
  protected readonly ctWindowName = 'isgHelp';

  /** The Help Centre root, and the only route that needs context resolving. */
  protected readonly ctHelpRoot = '/help';

  // ----------------- State -------------------------------------------------

  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly api = inject(HelpApiService);
  private readonly helpContext = inject(HelpContextService);

  // ----------------- Public methods ----------------------------------------

  /**
   * Opens a Help route in the Help tab.
   *
   * For the Help root this first asks the resolver where the reader was, so the new tab
   * opens on that article rather than the Help home. For a specific Help route - a
   * section slug or an article id - there is nothing to resolve and it opens directly.
   */
  public open(url: string | null | undefined): void {

    const target = (url ?? '').trim() || this.ctHelpRoot;

    if (!this.isHelpRoot(target)) {
      this.openWindow(target);
      return;
    }

    const hint = this.helpContext.current();

    if (!hint) {
      this.openWindow(target);
      return;
    }

    const req = new RequestHelpContext();

    req.linkVarName = hint.linkVarName;
    req.linkUrlPath = hint.linkUrlPath;

    // One article is all this needs; the shell reads the rest of the tree itself.
    req.includeRelated = false;

    this.api.helpContextGet$(req)
      .pipe(take(1))
      .subscribe({
        // Every failure path opens Help anyway. Someone asking for help must never be
        // worse off for asking, so a resolver problem costs them position, not access.
        next: resp => {

          const item = resp?.data;
          const resolved = item?.id && !item.isFallback
            ? this.ctHelpRoot + '/' + item.id
            : target;

          this.openWindow(resolved);
        },
        error: () => this.openWindow(target)
      });
  }

  // ----------------- Helpers -----------------------------------------------

  /**
   * True for the Help root, which is the only Help route carrying no destination of its
   * own. Query strings and fragments are ignored so a decorated link still counts.
   */
  public isHelpRoot(url: string | null | undefined): boolean {

    const path = (url ?? '').split('?')[0].split('#')[0];

    return path === this.ctHelpRoot;
  }

  /** True for any route inside Help - the reader's Help, not the CMS. */
  public isHelpRoute(url: string | null | undefined): boolean {

    const path = (url ?? '').split('?')[0].split('#')[0];

    return path === this.ctHelpRoot || path.startsWith(this.ctHelpRoot + '/');
  }

  /**
   * Opens the named Help tab and brings it forward.
   *
   * prepareExternalUrl rather than string concatenation, so this still points at the
   * right place if the application is ever hosted under a base href.
   *
   * noopener is deliberately NOT set: the tab is our own application on our own origin,
   * and it is named so it can be found again.
   */
  protected openWindow(route: string): void {

    const href = this.location.prepareExternalUrl(
      this.router.serializeUrl(this.router.createUrlTree([route])));

    const opened = window.open(href, this.ctWindowName);

    // A blocked or refused window returns null. Fall back to routing in place rather
    // than swallowing the click - losing your position beats nothing happening.
    if (!opened) {
      this.router.navigateByUrl(route);
      return;
    }

    opened.focus();
  }
}
